/**
 * Client-side CSV Export for Warehouse Items
 *
 * Converts Item[] data to CSV and triggers browser download.
 * Supports UTF-8 BOM for correct Vietnamese display in Excel.
 */

import dayjs from 'dayjs'
import type { Item } from '../types/response'

/** CSV column definition: header label + extract function */
interface CsvColumn {
    header: string
    extract: (item: Item) => string
}

/** All available columns for CSV export */
const CSV_COLUMNS: CsvColumn[] = [
    { header: 'Type', extract: (i) => i.item_type || '' },
    { header: 'Name', extract: (i) => i.name || '' },
    { header: 'Service Tag', extract: (i) => i.service_tag || i.ref || '' },
    { header: 'Serial Number', extract: (i) => i.serial_number || '' },
    { header: 'Category', extract: (i) => i.category || '' },
    { header: 'Status', extract: (i) => i.status?.replace('_', ' ') || '' },
    { header: 'Condition', extract: (i) => i.condition || '' },
    {
        header: 'Location',
        extract: (i) => i.current_location?.name || '',
    },
    { header: 'Rack', extract: (i) => (i as any).rack?.code || '' },
    {
        header: 'Rack Position',
        extract: (i) => (i.rack_position_u ? `U${i.rack_position_u}` : ''),
    },
    { header: 'Supplier', extract: (i) => i.supplier?.name || '' },
    { header: 'Model', extract: (i) => i.model?.name || '' },
    { header: 'Owner', extract: (i) => i.owner || '' },
    { header: 'Quantity', extract: (i) => String(i.quantity ?? '') },
    { header: 'Unit', extract: (i) => i.unit || '' },
    {
        header: 'Purchase Date',
        extract: (i) =>
            i.purchase_date ? dayjs(i.purchase_date).format('YYYY-MM-DD') : '',
    },
    {
        header: 'Purchase Price',
        extract: (i) =>
            i.purchase_price != null ? String(i.purchase_price) : '',
    },
    { header: 'Currency', extract: (i) => i.purchase_currency || '' },
    {
        header: 'Warranty Start',
        extract: (i) =>
            i.warranty_start_date
                ? dayjs(i.warranty_start_date).format('YYYY-MM-DD')
                : '',
    },
    {
        header: 'Warranty End',
        extract: (i) =>
            i.warranty_end_date
                ? dayjs(i.warranty_end_date).format('YYYY-MM-DD')
                : '',
    },
    { header: 'Notes', extract: (i) => i.notes || '' },
    {
        header: 'Created At',
        extract: (i) =>
            i.created_at ? dayjs(i.created_at).format('YYYY-MM-DD HH:mm') : '',
    },
]

/** Escape a value for CSV (handle commas, quotes, newlines) */
const escapeCsvValue = (value: string): string => {
    if (
        value.includes(',') ||
        value.includes('"') ||
        value.includes('\n') ||
        value.includes('\r')
    ) {
        return `"${value.replace(/"/g, '""')}"`
    }
    return value
}

/** Convert Item[] to CSV string */
const itemsToCsv = (items: Item[]): string => {
    const header = CSV_COLUMNS.map((col) => escapeCsvValue(col.header)).join(
        ','
    )

    const rows = items.map((item) =>
        CSV_COLUMNS.map((col) => escapeCsvValue(col.extract(item))).join(',')
    )

    return [header, ...rows].join('\n')
}

/** Trigger browser download of a CSV string */
const downloadCsv = (csvContent: string, filename: string) => {
    // UTF-8 BOM so Excel opens Vietnamese characters correctly
    const BOM = '\uFEFF'
    const blob = new Blob([BOM + csvContent], {
        type: 'text/csv;charset=utf-8;',
    })

    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()

    // Cleanup
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
}

/**
 * Export items to CSV and trigger download.
 *
 * @param items - Array of Item objects (current page data)
 * @param category - Category tab name, used in filename
 */
export const exportItemsCsv = (items: Item[], category: string) => {
    if (items.length === 0) return false

    const csvContent = itemsToCsv(items)
    const timestamp = dayjs().format('YYYYMMDD_HHmm')
    const filename = `warehouse_${category}_${timestamp}.csv`

    downloadCsv(csvContent, filename)
    return true
}
